import { apiClient } from "@/config/api.config";

// Configuration constants
const FORM_STORAGE_KEY = "kyc_form_data";
const KYC_ID_KEY = "kyc_id";

/**
 * KYC Service for handling multi-step KYC process
 */
class KYCService {
  /**
   * Process a KYC form step
   * @param {number} step - The step number (1-5)
   * @param {object} formData - The form data for this step
   * @param {object} files - Any files to upload for this step
   * @returns {Promise} API response
   */
  async processStep(step, formData, files = null) {
    try {
      // Save form data to localStorage for recovery
      this.saveFormData(step, formData);

      // Get KYC ID if it exists (for steps after step 1)
      const kycId = localStorage.getItem(KYC_ID_KEY);

      // Prepare form data for API call
      const requestData = new FormData();

      // Add form data
      requestData.append("data", JSON.stringify(formData));

      // Add files if provided
      if (files) {
        Object.entries(files).forEach(([key, file]) => {
          if (file instanceof File) {
            requestData.append(key, file);

            // If there's a password for this file, add it
            const passwordKey = `${key}Password`;
            if (formData[passwordKey]) {
              requestData.append(passwordKey, formData[passwordKey]);
            }
          }
        });
      }

      // Determine endpoint and method based on step and whether we're creating or updating
      let endpoint = "";
      let method = "post";

      if (step === 1 && !kycId) {
        // Initial submission
        endpoint = "/auth/kyc/submit";
      } else {
        // Update existing KYC
        endpoint = `/auth/kyc/update/${kycId}`;
        method = "put";
      }

      // Make API call
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      const response =
        method === "post"
          ? await apiClient.post(endpoint, requestData, config)
          : await apiClient.put(endpoint, requestData, config);

      // Store KYC ID for further steps
      if (response.data.kycDetails?.kycId) {
        localStorage.setItem(KYC_ID_KEY, response.data.kycDetails.kycId);
      }

      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error(`Failed to process KYC step ${step}`)
      );
    }
  }

  /**
   * Get the current user's KYC profile
   * @returns {Promise} User profile data
   */
  async getProfile() {
    try {
      const response = await apiClient.get("/auth/kyc/userprofile");

      // If this is the first time retrieving profile and it has KYC ID, save it
      if (response.data?.data?.user?.kycDetails?.kycId) {
        localStorage.setItem(
          KYC_ID_KEY,
          response.data.data.user.kycDetails.kycId
        );
      }

      return response.data.data.user;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch user profile");
    }
  }

  /**
   * Check the status of user's KYC verification
   * @returns {Promise} KYC status data
   */
  async checkKycStatus() {
    try {
      const response = await apiClient.get("/auth/status");
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to check KYC status");
    }
  }

  /**
   * Get a document for viewing/downloading
   * @param {string} documentType - Type of document to download
   * @returns {Promise} Document data
   */
  async getDocument(documentType) {
    try {
      // Get KYC ID from localStorage or try to fetch from profile
      let kycId = localStorage.getItem(KYC_ID_KEY);

      if (!kycId) {
        const profileData = await this.getProfile();
        kycId = profileData?.kycDetails?.kycId;

        if (!kycId) {
          throw new Error("KYC record not found");
        }
      }

      const response = await apiClient.get(
        `/auth/kyc/document/${kycId}/${documentType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const contentType = response.headers["content-type"];
      const blob = new Blob([response.data], { type: contentType });

      // Determine file extension from content type
      const mimeToExt = {
        "application/pdf": "pdf",
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/png": "png",
        "image/gif": "gif",
        "application/msword": "doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          "docx",
      };

      const fileExtension = mimeToExt[contentType] || "unknown";

      // Create and trigger download
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${documentType}.${fileExtension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(link.href);

      return true;
    } catch (error) {
      throw new Error("Failed to download document");
    }
  }

  /**
   * Save form data to local storage organized by step/section
   * @param {number} step - The step number (1-5)
   * @param {object} formData - The form data for this step
   * @returns {boolean} Success status
   */
  saveFormData(step, formData) {
    try {
      // Get existing form data from localStorage
      const existingData = this.getSavedFormData() || {};

      // Map step to form section
      const sectionMap = {
        1: "basicInfo",
        2: "dematDetails",
        3: "bankDetails",
        4: "documents",
        5: "submission",
      };

      const section = sectionMap[step] || `step${step}`;

      // Handle file references separately (store only file names)
      const fileReferences = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value instanceof File) {
          fileReferences[key] = value.name;
          // Remove file objects from the data to be stored
          delete formData[key];
        }
      });

      // Update the section with new form data
      const updatedData = {
        ...existingData,
        [section]: {
          ...formData,
          ...(Object.keys(fileReferences).length > 0 && { fileReferences }),
        },
      };

      // Save to localStorage
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(updatedData));

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get saved form data from local storage
   * @param {number} step - Optional step number to retrieve specific data
   * @returns {object} Saved form data
   */
  getSavedFormData(step = null) {
    try {
      const data = localStorage.getItem(FORM_STORAGE_KEY);
      const parsedData = data ? JSON.parse(data) : null;

      if (!parsedData) return null;

      // If step is provided, return only that section
      if (step !== null) {
        const sectionMap = {
          1: "basicInfo",
          2: "dematDetails",
          3: "bankDetails",
          4: "documents",
          5: "submission",
        };

        const section = sectionMap[step] || `step${step}`;
        return parsedData[section] || null;
      }

      return parsedData;
    } catch (error) {
      return null;
    }
  }

  /**
   * Clear saved form data and KYC ID
   */
  clearSavedFormData() {
    localStorage.removeItem(FORM_STORAGE_KEY);
    localStorage.removeItem(KYC_ID_KEY);
  }

  /**
   * Legacy method: Submit entire KYC form (maps to step 1)
   * @param {object} formData - Complete form data
   * @returns {Promise} API response
   */
  async submit(formData) {

    // Extract files from form data
    const files = {};

    // Process step 1 (basic info)
    return this.processStep(1, formData, files);
  }

  /**
   * Legacy method: Update KYC form (determines step based on data)
   * @param {string} kycId - KYC ID
   * @param {object} formData - Form data to update
   * @returns {Promise} API response
   */
  async update(kycId, formData) {

    // Store KYC ID for further steps
    localStorage.setItem(KYC_ID_KEY, kycId);

    // Extract files from form data
    const files = {};
    ["panCard", "bankDocuments", "dematDocuments", "aadhaarFront", "aadhaarBack"].forEach(
      (key) => {
        if (formData[key] instanceof File) {
          files[key] = formData[key];
        }
      }
    );

    // Determine which step this is based on data structure
    let step = 1;

    if (
      formData.dematDetails &&
      Object.keys(formData.dematDetails).length > 0
    ) {
      step = 2;
    } else if (
      formData.bankDetails &&
      Object.keys(formData.bankDetails).length > 0
    ) {
      step = 3;
    } else if (
      formData.panCard instanceof File ||
      formData.bankDocuments instanceof File ||
      formData.dematDocuments instanceof File ||
      formData.aadhaarFront instanceof File ||
      formData.aadhaarBack instanceof File
    ) {
      step = 4;
    } else if (formData.type === "final" || formData.complete) {
      step = 5;
    }

    return this.processStep(step, formData, files);
  }
  //view documnet
  async getKycDocument(kycId, documentType, token) {
    return await apiClient.get(`/auth/kyc/${kycId}/document/${documentType}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: "blob",
    });
  }

  async downloadKycDocument(kycId, documentType, token) {
    return await apiClient.get(
      `/auth/kyc/${kycId}/document/${documentType}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      }
    );
  }






  // Get KYC details by ID
  async getKycDetails(id) {
    try {
      const response = await apiClient.get(`/admin/kyc/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Verify KYC application
  async verifyKyc(id) {
    try {
      const response = await apiClient.post(`/admin/kyc/${id}/verify`, {
        status: 'verified'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  // Reject KYC application
  async rejectKyc(id, reason) {
    try {
      const response = await apiClient.post(`/admin/kyc/${id}/reject`, {
        reason
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Mark KYC as in-review
  async markInReview(id) {
    try {
      const response = await apiClient.post(`/admin/kyc/${id}/mark-in-review`, {});
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Update KYC status (general method)
  async updateKycStatus(id, status, reason = null) {
    try {
      let endpoint, data;

      switch (status) {
        case 'verified':
          endpoint = 'verify';
          data = { status: 'verified' };
          break;
        case 'rejected':
          endpoint = 'reject';
          data = { reason };
          break;
        case 'in-review':
          endpoint = 'mark-in-review';
          data = {};
          break;
        default:
          throw new Error('Invalid status');
      }

      const response = await apiClient.post(`/api/v1/admin/kyc/${id}/${endpoint}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Edit KYC details as Admin
  async updateKycDetails(id, updateData) {
    try {
      const response = await apiClient.patch(`/admin/kyc/${id}/update`, updateData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }



}

export default new KYCService();
